import Image from 'next/image'
import { useState, useEffect, useRef } from 'react'
import {
  Burger,
  BurgerLine,
  Icons,
  MenuContainer,
  MenuItemText,
  SearchContainer,
  SearchTrigger,
  UserInitials,
  Wrapper,
  DesktopLogo,
  SantaHat,
  TopbarContainer,
  MenuItemWrapper,
  CartIcon,
  Total,
  IconWrapper,
  MenuItemLinkWrapper,
} from './Topbar.styles'
import HappyHour from './HappyHour'
import Icon from './Icons'
import OffCanvasMenu from './OffCanvasMenu'
import { useAuthSession } from '@/hooks/AuthSessionHook'
import Link from 'next/link'
import { useRouter } from 'next/router'
import SearchModal from './SearchModal'
import { useDefaultProperties } from '@/hooks/DefaultPropertiesHook'
import { ModalTypes, SearchInteractionInput } from '@/types'
import { useAnalyticsSearchModalInteraction } from '@/hooks/AnalyticsSearchModalInteractionHook'
import { useTheme } from 'styled-components'
import { isChristmas } from '@/helpers/DateHelper'
import { Button } from '@big-red-group/storefront-common-ui'
import { useCart } from '@big-red-group/storefront-common-checkout'
import { DEFAULT_LANGUAGE, getUrlPrefix } from '@/helpers/UrlHelper'
import { processCategoryTypes } from '@/helpers/TopbarHelper'
import ProductTile from './ProductTile'
import { SearchPopup } from '@big-red-group/storefront-common-ui'
import 'swiper/css'
import { useProductClicked } from '@/hooks/AnalyticsProductHook'
import { useElementClick, useNavigationClick } from '@/hooks/HTMLElementClickHook'

const Topbar = () => {
  const placement = 'Topbar'
  const searchType: ModalTypes = 'search'

  const defaultProperties = useDefaultProperties()
  const allowCustomerLogins = defaultProperties?.agentConfiguration?.allowCustomerLogins
  const baseUrl = getUrlPrefix(defaultProperties?.agentConfiguration?.handlerKey)
  const trackSearchModalInteraction = useAnalyticsSearchModalInteraction(true)
  const trackProductClicked = useProductClicked()
  const trackButtonClicked = useElementClick('Button')
  const trackLinkClicked = useElementClick('Link')
  const trackNavClicked = useNavigationClick()

  const router = useRouter()
  const { lang } = router.query
  const { data: cart, mutate } = useCart('cart', baseUrl)
  const total = cart?.items?.length || 0

  // Auth
  const { authenticated, unauthenticated, loading, user } = useAuthSession()

  const theme = useTheme()

  const destinations = defaultProperties?.topbarConfiguration?.destinations
  const categories = processCategoryTypes(defaultProperties?.topbarConfiguration?.categoryTypes)
  const displayableLinks = defaultProperties?.topbarConfiguration?.displayableLinks
  let helpfulLinks = defaultProperties?.topbarConfiguration?.helpfulLinks
  const happyHour = defaultProperties?.happyHourEvent
  const isBrgAgent = defaultProperties?.agentConfiguration?.isBrgAgent
  const [isOffCanvasMenuOpen, setOffCanvasMenuOpen] = useState(false)
  const [offCanvasSubmenu, setOffCanvasSubmenu] = useState('')
  const [offCanvasThirdmenu, setOffCanvasThirdMenu] = useState('')

  // TODO: Ugly hack to be removed after nbr campaign
  if (defaultProperties?.agentConfiguration?.partnerCode === 'tropicaldollars') {
    helpfulLinks = [
      { label: 'FAQs', url: 'https://cdn.experienceoz.com.au/assets/organisation/15677/termsAndConditions/161644.pdf' },
    ]
  }

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const firstInitial = firstName && firstName.length > 0 ? firstName.charAt(0) : ''
    const lastInitial = lastName && lastName.length > 0 ? lastName.charAt(0) : ''
    return `${firstInitial}${lastInitial}`.toUpperCase()
  }

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  const handleSearchModalOpen = () => {
    handleCloseOffCanvasMenu()
    try {
      trackSearchModalInteraction({ toggle: 'on', placement, type: searchType })
    } catch (error) {
      console.error(error)
    }
  }

  const handleSearchModalClose = (performedSearch = false, trackingExtras?: any) => {
    try {
      if (!performedSearch) {
        const extras = {
          autocorrectValues: trackingExtras?.suggestions,
          totalProducts: trackingExtras?.products?.length,
          displayProducts: trackingExtras?.products?.slice(0, 4),
          searchedInput: trackingExtras?.searchedInput,
          searchedValue: trackingExtras?.searchedValue,
          suggestedCategories: trackingExtras?.suggestedCategories?.slice(0, 6),
          suggestedLocations: trackingExtras?.suggestedRegions?.slice(0, 6),
        }
        trackSearchModalInteraction({ toggle: 'off', placement, type: searchType, extras: extras })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const trackSearch = (input: SearchInteractionInput) => {
    try {
      trackSearchModalInteraction({ toggle: 'search', placement, type: searchType, interaction: input })
    } catch (error) {
      console.error(error)
    }
  }

  const handleNavigate = (param: any) => {
    if (param.extras.action === 'onPressEnter') {
      trackSearch({
        isProductsSearched: true,
        searchedInput: param.extras?.searchedInput,
        searchedValue: param.extras?.searchedValue,
        autocorrectValues: param.extras?.suggestions,
        isLocationSearched: param.type === 'region' ? true : false,
        isCategorySearched: param.type === 'category' ? true : false,
        suggestedCategories: param.extras?.suggestedCategories?.slice(0, 6),
        suggestedLocations: param.extras?.suggestedRegions?.slice(0, 6),
        totalProducts: param.extras?.products?.length,
        displayProducts: param.extras?.products?.slice(0, 4),
      })
    } else if (param.extras.action === 'onClick') {
      trackSearch({
        isProductsSearched: true,
        searchedInput: param.extras?.searchedInput,
        searchedValue: param.extras?.searchedValue,
        autocorrectValues: param.extras?.suggestions,
        suggestedCategories: param.extras?.suggestedCategories?.slice(0, 6),
        suggestedLocations: param.extras?.suggestedRegions?.slice(0, 6),
        isAutoCorrectionClicked: param.extras?.isAutoCorrectionClicked,
        isSuggestedCategoryClicked: param.extras?.isSuggestedCategoryClicked,
        isCategorySearched: param.extras?.isCategorySearched,
        isSuggestedLocationClicked: param.extras?.isSuggestedRegionClicked,
        isLocationSearched: param.extras?.isRegionSearched,
        totalProducts: param.extras?.products?.length,
        displayProducts: param.extras?.products?.slice(0, 4),
      })
    } else if (param.extras.action === 'productClick') {
      trackSearch({
        isProductsSearched: true,
        searchedInput: param.extras?.searchedInput,
        searchedValue: param.extras?.searchedValue,
        autocorrectValues: param.extras?.suggestions,
        suggestedCategories: param.extras?.suggestedCategories?.slice(0, 6),
        suggestedLocations: param.extras?.suggestedRegions?.slice(0, 6),
        isProductClicked: param.extras?.isProductClicked,
        product: param.extras?.product,
        totalProducts: param.extras?.product?.length,
        displayProducts: param.extras?.products?.slice(0, 4),
        isAutoCorrectionClicked: param.extras?.isAutoCorrectionClicked,
        isRecentlyViewed: param.extras?.isRecentlyViewed,
      })
      trackProductClicked({
        product: param.extras?.product,
        position: param.extras?.position,
        isRecommended: true,
        listTitle: param.extras?.listTitle,
        isCarousel: true,
      })
    } else if (param.extras.action === 'seeMoreClick') {
      trackSearch({
        isProductsSearched: true,
        searchedInput: param.extras?.searchedInput,
        searchedValue: param.extras?.searchedValue,
        autocorrectValues: param.extras?.suggestions,
        suggestedCategories: param.extras?.suggestedCategories?.slice(0, 6),
        suggestedLocations: param.extras?.suggestedRegions?.slice(0, 6),
        totalProducts: param.extras?.products?.length,
        displayProducts: param.extras?.products?.slice(0, 4),
        isSeeMoreClicked: true,
      })
    }
  }

  const isHappyHourActive = () => {
    const currentTime = new Date()

    if (!happyHour) {
      return false
    }

    const startTime = new Date(happyHour.startTime)
    const endTime = new Date(happyHour.endTime)

    return currentTime >= startTime && currentTime <= endTime
  }

  const renderUser = () => {
    if (loading) return null

    const buttonStyle = { padding: '12px', fontSize: '12px', marginRight: '16px' }
    if (unauthenticated) {
      return (
        <Button
          style={buttonStyle}
          // TODO: fix once LOKI reponse returns correct agent info
          // onClick={() => {
          //   if (
          //     defaultProperties?.agentConfiguration?.allowCustomerLogins &&
          //     defaultProperties?.agentConfiguration?.agentOAuth2Configuration
          //   ) {
          //     signIn(defaultProperties?.agentConfiguration?.handlerKey)
          //   } else {
          //     router.push(`/${router.query?.lang ?? DEFAULT_LANGUAGE}/login`)
          //   }
          // }}
          onClick={(event) => {
            router.push(`/${router.query?.lang ?? DEFAULT_LANGUAGE}/login`)
            trackButtonClicked({ location: 'Topbar | Account', event })
          }}
          dataTestId="topbar-login"
        >
          Login
        </Button>
      )
    }

    if (authenticated) {
      return (
        <UserInitials
          onClick={(event) => {
            router.push(`/${lang}/account`)
            trackButtonClicked({ location: 'Topbar | Account', event })
          }}
          data-testid="initials"
        >
          {getInitials(user?.firstName, user?.lastName)}
        </UserInitials>
      )
    }

    return (
      <Button
        style={buttonStyle}
        onClick={(event) => {
          router.push(`/${router.query?.lang ?? DEFAULT_LANGUAGE}/login`)
          trackButtonClicked({ location: 'Topbar | Account', event })
        }}
        dataTestId="topbar-login"
      >
        Login
      </Button>
    )
  }

  const handleBurgerMenu = () => {
    if (isOffCanvasMenuOpen) {
      setOffCanvasMenuOpen(false)
      setOffCanvasSubmenu('')
      setOffCanvasThirdMenu('')
      return
    }
    setOffCanvasMenuOpen(true)
  }

  const handleOpenOffCanvasMenu = (submenu?: string) => {
    setOffCanvasMenuOpen(true)
    if (submenu) {
      setOffCanvasSubmenu(submenu)
    }
  }

  const handleCloseOffCanvasMenu = () => {
    setOffCanvasMenuOpen(false)
    setOffCanvasSubmenu('')
    setOffCanvasThirdMenu('')
  }

  const renderMainHeaderLinks = () => {
    if (!displayableLinks) return

    return (
      <>
        {displayableLinks.map((item, index) => {
          if (item.subLinks) {
            return (
              <MenuItemWrapper
                key={item.label}
                data-testid={`displayable-link-sublink-${index}`}
                onClick={() => {
                  handleOpenOffCanvasMenu(item.label)
                  setOffCanvasThirdMenu('')
                }}
              >
                <MenuItemText>{item.label}</MenuItemText>
                <Icon className="icon-right" name="chevronDown" width={16} height={17} />
              </MenuItemWrapper>
            )
          } else {
            return (
              <MenuItemLinkWrapper
                href={item.url!}
                key={item.label}
                color={item.colour}
                onClick={(event) => {
                  trackNavClicked({
                    menuType: `Navigation Menu | ${item.label}`,
                    text: item.label,
                    url: item.url,
                    event,
                  })
                }}
                data-testid={`displayable-link-${index}`}
              >
                {item.iconUrl && (
                  <IconWrapper data-testid={`displayable-link-icon-${index}`}>
                    <Image src={item.iconUrl} alt={item.label} width={16} height={16} />
                  </IconWrapper>
                )}
                {item.label}
              </MenuItemLinkWrapper>
            )
          }
        })}
      </>
    )
  }

  const topbarRef = useRef<HTMLInputElement>(null)
  const [topbarHeight, setTopbarHeight] = useState(0)
  useEffect(() => {
    if (topbarRef.current !== null) {
      setTopbarHeight(topbarRef.current.clientHeight)
    }
  }, [])

  return (
    <TopbarContainer ref={topbarRef} id="top-bar-wrapper">
      {isHappyHourActive() && (
        <HappyHour content={happyHour?.content ?? ''} mobileContent={happyHour?.mobileContent ?? ''} />
      )}
      <Wrapper>
        <SearchModal
          initialActiveInput="location"
          isOpen={isSearchModalOpen}
          type={searchType}
          placement={placement}
          onClose={handleSearchModalClose}
          onSearch={trackSearchModalInteraction}
          destinations={destinations!}
          part1Type=""
          part2Type=""
          part1=""
          part2=""
          part3=""
        />
        {categories && (
          <OffCanvasMenu
            topbarHeight={topbarHeight}
            triggerLogin={() => router.push(`/${router.query?.lang ?? DEFAULT_LANGUAGE}/login`)}
            isOpen={isOffCanvasMenuOpen}
            submenuToggled={offCanvasSubmenu}
            thirdMenuToggled={offCanvasThirdmenu}
            onClose={handleCloseOffCanvasMenu}
            onMenuChange={setOffCanvasSubmenu}
            onThirdMenuChange={setOffCanvasThirdMenu}
            destinations={destinations}
            categories={categories}
            displayableLinks={displayableLinks}
            helpfulLinks={helpfulLinks}
          />
        )}
        <MenuContainer>
          <Burger data-testid="burger-menu" isToggled={isOffCanvasMenuOpen} onClick={handleBurgerMenu}>
            <BurgerLine className="line-1" />
            <BurgerLine className="line-2" />
            <BurgerLine className="line-3" />
          </Burger>
          <Link href={`/${lang}`}>
            <DesktopLogo showOnMobile>
              {isChristmas() && isBrgAgent && (
                <SantaHat>
                  <Icon name="santaHat" />
                </SantaHat>
              )}
              <Image
                src={theme?.logo?.image ? theme.logo.image : '/logos/exoz-logo.svg'}
                alt="Experience Oz"
                width={isBrgAgent ? 160 : 140}
                height={isBrgAgent ? 65 : 70}
              />
            </DesktopLogo>
          </Link>
          <MenuItemWrapper
            data-testid={`destinations-topbar`}
            onClick={() => {
              handleOpenOffCanvasMenu('Destinations')
              setOffCanvasThirdMenu('')
            }}
          >
            <MenuItemText>Destinations</MenuItemText>
            <Icon className="icon-right" name="chevronDown" width={16} height={17} />
          </MenuItemWrapper>
          {categories?.map((categoryType) => (
            <MenuItemWrapper
              key={categoryType.categoryType}
              data-testid={`${categoryType.categoryType?.toLowerCase()}-topbar`}
              onClick={() => {
                handleOpenOffCanvasMenu(categoryType.categoryType)
                setOffCanvasThirdMenu('')
              }}
            >
              <MenuItemText>{categoryType.label}</MenuItemText>
              <Icon className="icon-right" name="chevronDown" width={16} height={17} />
            </MenuItemWrapper>
          ))}
          {renderMainHeaderLinks()}
        </MenuContainer>
        <SearchContainer>
          <Icons>
            <SearchPopup
              lang={`${lang || 'en'}`}
              push={router.push}
              onSearch={async (param) => {
                const { query, limit } = param
                const body: any = {
                  query,
                  limit,
                  lang,
                  regions: defaultProperties?.searchProperties?.regions?.map((a) => a.urlSegment),
                }
                const res = await (
                  await fetch(`/api/search`, {
                    method: 'POST',
                    body: JSON.stringify(body),
                  })
                ).json()
                return res
              }}
              onToggleOpen={({ isOpen, data }) => {
                if (isOpen) {
                  handleSearchModalOpen()
                  return
                } else if (data) {
                  handleSearchModalClose(data.isPerformedSearch, data.trackingExtras)
                }
              }}
              onNavigate={handleNavigate}
              platform="exoz"
              popularUrl={`/things-to-do/hot-deals`}
              onProductRender={(product) => (
                <ProductTile
                  id={product.id}
                  title={product.name}
                  supplier={product.operatorName}
                  price={product.retailPrice}
                  fromPrice={product.fromPrice}
                  region={product.primaryRegion}
                  canonicalRegionUrlSegment={product.canonicalRegionUrlSegment}
                  image={product.images?.[0] || ''}
                  url={product.url}
                  onClick={() => {}}
                  rating={product.rating}
                />
              )}
            />
            {allowCustomerLogins && renderUser()}
            {/* cart */}
            <Link
              href="/en/order/checkout"
              data-testid="cart-icon"
              aria-label="Checkout"
              onClick={(event) => trackLinkClicked({ location: 'Topbar | Cart', event })}
            >
              <CartIcon>
                {total > 0 && <Total>{total}</Total>}
                <Icon name="shoppingBag" />
              </CartIcon>
            </Link>
          </Icons>
        </SearchContainer>
      </Wrapper>
    </TopbarContainer>
  )
}

export default Topbar
